import { useState, useCallback, useRef } from 'react';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

export interface Suggestion {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
  types?: string[];
}

interface UsePlacesAutocompleteReturn {
  suggestions: Suggestion[];
  loading: boolean;
  fetchSuggestions: (input: string) => Promise<void>;
  clearSuggestions: () => void;
}

export function usePlacesAutocomplete(
  type: 'establishment' | 'address' = 'address'
): UsePlacesAutocompleteReturn {
  const { isLoaded } = useGoogleMapsLoader();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);

  const getService = useCallback(() => {
    if (!isLoaded) return null;
    if (!serviceRef.current) {
      serviceRef.current = new google.maps.places.AutocompleteService();
    }
    return serviceRef.current;
  }, [isLoaded]);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input || input.length < 3) {
        setSuggestions([]);
        return;
      }

      const service = getService();
      if (!service) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await service.getPlacePredictions({
          input,
          types: [type],
        });

        const mapped: Suggestion[] = (response.predictions || []).map((p) => ({
          description: p.description,
          place_id: p.place_id,
          structured_formatting: p.structured_formatting
            ? {
                main_text: p.structured_formatting.main_text,
                secondary_text: p.structured_formatting.secondary_text,
              }
            : undefined,
          types: p.types,
        }));

        setSuggestions(mapped);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [type, getService]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return { suggestions, loading, fetchSuggestions, clearSuggestions };
}
